import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, ArrowRight } from 'lucide-react';

/**
 * Left-edge "Enter The Locker Room" tab that slides in once the visitor
 * has scrolled past the hero. Sticks to the left side mid-height, on top
 * of the page content. Hidden on:
 *   - The Locker Room page itself
 *   - The auth’d app (/app/*)
 *   - Focus-mode funnels (/assessment, /quiz)
 *   - The dedicated Playlists page
 * Hidden on mobile (< sm) to keep the launcher unobtrusive on small
 * screens — the footer/nav links cover those visitors instead.
 */
const HIDE_ON = ['/locker-room', '/app', '/assessment', '/quiz', '/playlists'];
const SHOW_AFTER_SCROLL_PX = 540;

export function LockerRoomCTA() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        setVisible(window.scrollY > SHOW_AFTER_SCROLL_PX);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  const shouldHide = HIDE_ON.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  if (shouldHide) return null;

  return (
    <Link
      to="/locker-room"
      className={`hidden sm:flex fixed left-0 top-1/2 -translate-y-1/2 z-30 items-center gap-2 pl-2.5 pr-3.5 py-3 rounded-r-2xl bg-[#0B0C0D] text-white shadow-2xl shadow-black/40 ring-1 ring-white/10 hover:ring-[#4FBCC7]/50 hover:pl-4 transition-all ${
        visible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
      }`}
      data-testid="locker-room-cta"
      aria-label="Open The Locker Room"
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#004C54]">
        <Lock className="h-3.5 w-3.5" />
      </span>
      <span className="flex flex-col items-start leading-tight pr-0.5">
        <span className="font-mono-accent uppercase tracking-[0.18em] text-[8.5px] text-[#4FBCC7] font-semibold">
          Free + Premium
        </span>
        <span className="text-[12.5px] font-semibold inline-flex items-center gap-1">
          The Locker Room
          <ArrowRight className="h-3 w-3 opacity-70" />
        </span>
      </span>
    </Link>
  );
}

export default LockerRoomCTA;
